/**
 * POI (Points of Interest) icon catalog.
 *
 * Kept intentionally small (~12 icon slots) so the host can pick quickly
 * without paralysis. Each entry maps to a lucide-react icon name that is
 * available in the current bundle.
 *
 * Consumed by:
 *   • `/app/frontend/src/components/POIEditor.jsx` (admin picker)
 *   • `/app/frontend/src/pages/PropertyLanding.jsx` (public list below map)
 */

export const POI_ICONS = [
  { id: "landmark",    label: "Attrazione",       lucide: "Landmark",     color: "#a855f7" },
  { id: "restaurant",  label: "Ristorante",       lucide: "UtensilsCrossed", color: "#f97316" },
  { id: "bar",         label: "Bar / Caffè",      lucide: "Coffee",       color: "#a16207" },
  { id: "beach",       label: "Spiaggia / Mare",  lucide: "Waves",        color: "#06b6d4" },
  { id: "airport",     label: "Aeroporto",        lucide: "Plane",        color: "#0284c7" },
  { id: "port",        label: "Porto / Imbarchi", lucide: "Anchor",       color: "#0369a1" },
  { id: "station",     label: "Stazione / Treno", lucide: "TrainFront",   color: "#4f46e5" },
  { id: "metro",       label: "Metro",            lucide: "MetroM",       color: "#DC2626", custom: "MetroM" },
  { id: "bus",         label: "Bus",              lucide: "Bus",          color: "#059669" },
  { id: "supermarket", label: "Supermercato",     lucide: "ShoppingCart", color: "#16a34a" },
  { id: "pharmacy",    label: "Farmacia",         lucide: "Cross",        color: "#dc2626" },
  { id: "hospital",    label: "Ospedale",         lucide: "Hospital",     color: "#e11d48" },
  { id: "park",        label: "Parco / Natura",   lucide: "Trees",        color: "#15803d" },
  { id: "church",      label: "Chiesa / Monumento", lucide: "Church",     color: "#78716c" },
  { id: "museum",      label: "Museo",            lucide: "Building2",    color: "#7c3aed" },
  { id: "shopping",    label: "Shopping",         lucide: "ShoppingBag",  color: "#db2777" },
  { id: "gym",         label: "Palestra",         lucide: "Dumbbell",     color: "#334155" },
  { id: "parking",     label: "Parcheggio",       lucide: "SquareParking",color: "#2563eb" },
  { id: "gas",         label: "Distributore",     lucide: "Fuel",         color: "#374151" },
  { id: "wifi",        label: "Wi-Fi / Punto",    lucide: "Wifi",         color: "#0ea5e9" },
];

export const getPoiIcon = (iconId) =>
  POI_ICONS.find((i) => i.id === iconId) || POI_ICONS[0];

/**
 * Resolve an icon component for a given POI icon id. Returns a React
 * component that can be used as `<Icon className="…" />`. Handles both
 * Lucide icons (looked up in the passed `LucideIcons` bundle) and custom
 * icons registered via `CUSTOM_POI_ICONS` (e.g. Metro M red badge).
 */
import MetroM from "../components/icons/MetroM";

const CUSTOM_POI_ICONS = {
  MetroM,
};

export const resolvePoiIconComponent = (iconId, LucideIcons, fallback = null) => {
  const meta = getPoiIcon(iconId);
  if (meta.custom && CUSTOM_POI_ICONS[meta.custom]) {
    return CUSTOM_POI_ICONS[meta.custom];
  }
  const Ico = LucideIcons?.[meta.lucide];
  return Ico || fallback || LucideIcons?.MapPin;
};

// Labels of the meta fields, translated in the 5 supported landing languages.
// Names of POIs themselves are translated at save time by Gemini (see backend).
export const POI_LABELS = {
  it: { section: "Cosa c'è nei dintorni", walk: "a piedi", drive: "in auto", meters: "m", km: "km", min: "min" },
  en: { section: "What's nearby",         walk: "on foot", drive: "by car",  meters: "m", km: "km", min: "min" },
  fr: { section: "Aux alentours",         walk: "à pied",  drive: "en voiture", meters: "m", km: "km", min: "min" },
  es: { section: "Qué hay cerca",         walk: "a pie",   drive: "en coche", meters: "m", km: "km", min: "min" },
  de: { section: "In der Nähe",           walk: "zu Fuß",  drive: "mit dem Auto", meters: "m", km: "km", min: "min" },
};
